import React, { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, Button, Badge, Alert, Spinner, Container } from 'react-bootstrap';
import { Toast } from 'primereact/toast';
import { useAuth } from '../modules/auth';
import { API_URL } from '../modules/auth/core/_requests';
import axios from 'axios';
// import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

interface Sound {
  id: string;
  name: string;
  file: string;
  description: string;
}

interface ConnectedUser {
  userId: number;
  socketId: string;
  isConnected: boolean;
  email?: string;
  // Status information
  isWindowFocused?: boolean | null;
  isTabVisible?: boolean | null;
  lastActiveTime?: number | null;
  screenResolution?: string | null;
  timezone?: string | null;
  language?: string | null;
  pageUrl?: string | null;
  batteryLevel?: number | null;
  isCharging?: boolean | null;
  userAgent?: string | null;
  // Sound feedback
  lastSoundStatus?: string | null;
  lastSoundFile?: string | null;
  lastSoundTime?: number | null;
  soundFeedbackCount?: number;
  // Derived status
  isActivelyViewing?: boolean;
  timeSinceLastActive?: number | null;
}

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
}

export const SoundboardDashboard: React.FC = () => {
  const { currentUser } = useAuth();
  const toast = useRef<Toast>(null);
  
  const [sounds, setSounds] = useState<Sound[]>([]);
  const [connectedUsers, setConnectedUsers] = useState<ConnectedUser[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [playingSound, setPlayingSound] = useState<string | null>(null);
  const [volume, setVolume] = useState(0.7); // Default volume level

  useEffect(() => {
    loadData();
    const interval = setInterval(loadConnectedUsers, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadSounds(),
        loadUsers(),
        loadConnectedUsers()
      ]);
    } catch (error) {
      console.error('Failed to load soundboard data:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load system data',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSounds = async () => {
    const response = await axios.get(`${API_URL}/api/sound/sounds`);
    setSounds(response.data.sounds);
  };

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`);
      setUsers(response.data.users || []);
    } catch (error) {
      console.log('Users endpoint not available, using connected users only');
    }
  };

  const loadConnectedUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/sound/connected-users`);
      setConnectedUsers(response.data.users);
    } catch (error) {
      console.error('Failed to load connected users:', error);
    }
  };

  const playSound = async (sound: Sound, targetUserId: number) => {
    if (!targetUserId) {
      toast.current?.show({
        severity: 'warn',
        summary: 'No Target Selected',
        detail: 'Please select a target user from the connected sessions',
        life: 3000
      });
      return;
    }

    try {
      setPlayingSound(sound.id);
      
      await axios.post(`${API_URL}/api/sound/trigger`, {
        targetUserId,
        soundFile: sound.file,
        volume: volume
      });

      toast.current?.show({
        severity: 'success',
        summary: 'Audio Dispatched',
        detail: `"${sound.name}" sent to ${getUserName(targetUserId)} at ${Math.round(volume * 100)}% volume`,
        life: 4000
      });

    } catch (error: any) {
      console.error('Failed to trigger sound:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Dispatch Failed',
        detail: error.response?.data?.message || 'Failed to send audio signal',
        life: 3000
      });
    } finally {
      setTimeout(() => setPlayingSound(null), 1000);
    }
  };

  const getUserName = (userId: number): string => {
    // First try to get email from connected users data
    const connectedUser = connectedUsers.find(u => u.userId === userId);
    if (connectedUser?.email) {
      return connectedUser.email;
    }
    
    // Fallback to users endpoint if available
    const user = users.find(u => u.id === userId);
    if (user) {
      return `${user.first_name} ${user.last_name}`;
    }
    
    // Final fallback to user ID
    return `User ${userId}`;
  };

  const isUserConnected = (userId: number): boolean => {
    return connectedUsers.some(cu => cu.userId === userId);
  };

  if (loading) {
    return (
      <Container fluid className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <p className="mt-3 text-muted">Loading Audio Control Center...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Toast ref={toast} />
      
      {/* Header Section */}
      <Row className="mb-5">
        <Col xs={12}>
          <div className="text-center">
            <h1 className="display-5 fw-bold text-primary mb-3">
              <i className="pi pi-microphone-1 me-3"></i>
              Audio Control Center
            </h1>
            <p className="lead text-muted mb-0">
              Enterprise Audio Management System
            </p>
            <hr className="w-25 mx-auto mt-4 mb-0" style={{ height: '3px', background: 'linear-gradient(90deg, #0d6efd, #198754)' }} />
          </div>
        </Col>
      </Row>

      {/* Active Sessions Section */}
      <Row className="mb-5">
        <Col xs={12}>
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-primary text-white py-3">
              <Row className="align-items-center">
                <Col>
                  <h5 className="mb-0 fw-semibold">
                    <i className="pi pi-users me-2"></i>
                    Active User Sessions
                  </h5>
                </Col>
                <Col xs="auto">
                  <Badge bg="light" text="dark" className="fs-6 px-3 py-2">
                    {connectedUsers.length} Connected
                  </Badge>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="p-4">
              {connectedUsers.length === 0 ? (
                <Alert variant="info" className="text-center py-4 mb-0">
                  <i className="pi pi-info-circle me-2 fs-4"></i>
                  <div>
                    <h6 className="mt-2">No Active Sessions</h6>
                    <p className="mb-0 text-muted">Users must be logged into the platform to appear in this monitoring panel.</p>
                  </div>
                </Alert>
              ) : (
                <Row className="g-4">
                  {connectedUsers.map(user => (
                    <Col key={user.userId} md={6} lg={4} xl={3}>
                      <Card 
                        className={`h-100 cursor-pointer border-2 transition-all ${
                          selectedUser === user.userId 
                            ? 'border-success shadow-sm bg-light' 
                            : 'border-light hover-shadow'
                        }`}
                        onClick={() => setSelectedUser(user.userId)}
                        style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                      >
                        <Card.Body className="p-3">
                          <div className="d-flex align-items-center mb-3">
                            <div className="me-3">
                              <div className="position-relative">
                                <i 
                                  className={`pi pi-circle-fill ${
                                    user.isActivelyViewing ? 'text-success' : 
                                    user.isWindowFocused ? 'text-warning' : 'text-secondary'
                                  }`}
                                  style={{ fontSize: '1.2rem' }}
                                ></i>
                                <i 
                                  className="pi pi-user position-absolute top-50 start-50 translate-middle text-white"
                                  style={{ fontSize: '0.6rem' }}
                                ></i>
                              </div>
                            </div>
                            <div className="flex-grow-1 min-w-0">
                              <h6 className="mb-1 text-truncate fw-semibold">{getUserName(user.userId)}</h6>
                              <Badge bg={user.isActivelyViewing ? 'success' : user.isWindowFocused ? 'warning' : 'secondary'} className="small">
                                {user.isActivelyViewing ? 'Active' : user.isWindowFocused ? 'Focused' : 'Idle'}
                              </Badge>
                            </div>
                          </div>
                          
                          {/* Session Details */}
                          <div className="border-top pt-2">
                            <div className="small text-muted">
                              {user.screenResolution && (
                                <div className="d-flex align-items-center mb-1">
                                  <i className="pi pi-desktop me-2 text-muted"></i>
                                  <span>{user.screenResolution}</span>
                                </div>
                              )}
                              {user.timezone && (
                                <div className="d-flex align-items-center mb-1">
                                  <i className="pi pi-clock me-2 text-muted"></i>
                                  <span>{user.timezone}</span>
                                </div>
                              )}
                              {user.batteryLevel && (
                                <div className="d-flex align-items-center mb-1">
                                  <i className={`pi ${user.isCharging ? 'pi-bolt' : 'pi-mobile'} me-2 text-muted`}></i>
                                  <span>{user.batteryLevel}% {user.isCharging ? '(Charging)' : ''}</span>
                                </div>
                              )}
                              {user.lastSoundStatus && (
                                <div className="d-flex align-items-center">
                                  <i className={`pi me-2 ${
                                    user.lastSoundStatus === 'success' ? 'pi-check text-success' : 
                                    user.lastSoundStatus === 'failed' ? 'pi-times text-danger' : 
                                    'pi-info-circle text-warning'
                                  }`}></i>
                                  <span>
                                    Last audio: {user.lastSoundStatus}
                                    {user.lastSoundTime && (
                                      <span className="ms-1">({Math.round((Date.now() - user.lastSoundTime) / 1000)}s ago)</span>
                                    )}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="mt-2 pt-2 border-top text-center">
                            <small className="text-muted">Session ID: {user.userId}</small>
                            {user.soundFeedbackCount && user.soundFeedbackCount > 0 && (
                              <Badge bg="info" className="ms-2 small">
                                {user.soundFeedbackCount} signals sent
                              </Badge>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Target Selection Alert */}
      {selectedUser && (
        <Row className="mb-4">
          <Col xs={12}>
            <Alert variant="info" className="border-0 shadow-sm">
              <Row className="align-items-center">
                <Col>
                  <div className="d-flex align-items-center">
                    <i className="pi pi-bullseye me-3 fs-5"></i>
                    <div>
                      <strong>Target Session Selected:</strong>
                      <span className="ms-2">{getUserName(selectedUser)}</span>
                    </div>
                  </div>
                </Col>
                <Col xs="auto">
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    onClick={() => setSelectedUser(null)}
                  >
                    <i className="pi pi-times me-1"></i>
                    Clear Selection
                  </Button>
                </Col>
              </Row>
            </Alert>
          </Col>
        </Row>
      )}

      {/* Audio Configuration */}
      {selectedUser && (
        <Row className="mb-5">
          <Col xs={12}>
            <Card className="shadow-sm border-0">
              <Card.Header className="bg-warning text-dark py-3">
                <h6 className="mb-0 fw-semibold">
                  <i className="pi pi-sliders-h me-2"></i>
                  Audio Configuration
                </h6>
              </Card.Header>
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={8}>
                    <label htmlFor="volumeSlider" className="form-label fw-semibold mb-3">
                      Output Volume: <span className="text-primary">{Math.round(volume * 100)}%</span>
                    </label>
                    <input
                      id="volumeSlider"
                      type="range"
                      className="form-range"
                      min="0.1"
                      max="1.0"
                      step="0.1"
                      value={volume}
                      onChange={(e) => setVolume(parseFloat(e.target.value))}
                    />
                    <div className="d-flex justify-content-between small text-muted mt-1">
                      <span>10%</span>
                      <span>50%</span>
                      <span>100%</span>
                    </div>
                  </Col>
                  <Col md={4} className="text-center">
                    <Button
                      variant="outline-primary"
                      size="lg"
                      onClick={() => {
                        // Test volume by playing a short beep sound locally
                        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwDNXDU8N2QQAoUXrTp66hVFApGn+DyvmgfCEOVze7GfygJJneH5e2qWggNVZ7l8N2QQgwNTbPq7qxdFAlMprPw21MDNm/Y9duPOgYT');
                        audio.volume = volume;
                        audio.play().catch(() => {
                          toast.current?.show({
                            severity: 'info',
                            summary: 'Volume Test',
                            detail: `Audio level configured to ${Math.round(volume * 100)}%`,
                            life: 2000
                          });
                        });
                      }}
                    >
                      <i className="pi pi-volume-up me-2"></i>
                      Test Audio Level
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Audio Library */}
      <Row className="mb-5">
        <Col xs={12}>
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-success text-white py-3">
              <h5 className="mb-0 fw-semibold">
                <i className="pi pi-folder-open me-2"></i>
                Audio Signal Library
              </h5>
            </Card.Header>
            <Card.Body className="p-4">
              {sounds.length === 0 ? (
                <Alert variant="warning" className="text-center py-4 mb-0">
                  <i className="pi pi-exclamation-triangle me-2 fs-4"></i>
                  <div>
                    <h6 className="mt-2">No Audio Files Available</h6>
                    <p className="mb-0 text-muted">Audio library is currently empty. Please contact system administrator.</p>
                  </div>
                </Alert>
              ) : (
                <Row className="g-4">
                  {sounds.map(sound => (
                    <Col key={sound.id} md={6} lg={4} xl={3}>
                      <Card className="h-100 shadow-sm border-0 hover-shadow" style={{ transition: 'all 0.2s ease' }}>
                        <Card.Body className="text-center d-flex flex-column p-4">
                          <div className="mb-3">
                            <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px' }}>
                              <i className="pi pi-volume-up text-primary" style={{ fontSize: '1.5rem' }}></i>
                            </div>
                          </div>
                          <h6 className="mb-2 fw-semibold">{sound.name}</h6>
                          <p className="text-muted small mb-4 flex-grow-1">
                            {sound.description}
                          </p>
                          <Button
                            variant={playingSound === sound.id ? 'warning' : 'primary'}
                            disabled={!selectedUser || playingSound === sound.id}
                            onClick={() => selectedUser && playSound(sound, selectedUser)}
                            className="w-100"
                            size="lg"
                          >
                            {playingSound === sound.id ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Dispatching...
                              </>
                            ) : (
                              <>
                                <i className="pi pi-send me-2"></i>
                                Send Audio Signal
                              </>
                            )}
                          </Button>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* System Information */}
      <Row>
        <Col xs={12}>
          <Card className="bg-light border-0">
            <Card.Body className="p-4">
              <h6 className="fw-semibold mb-3">
                <i className="pi pi-info-circle me-2"></i>
                System Usage Guidelines
              </h6>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <h6 className="text-primary small fw-semibold">SETUP PROCESS</h6>
                    <ol className="small text-muted mb-0">
                      <li>Ensure target users are authenticated in the system</li>
                      <li>Select an active session from the monitoring panel</li>
                      <li>Configure desired audio output level</li>
                      <li>Choose audio signal from the library</li>
                    </ol>
                  </div>
                </Col>
                <Col md={6}>
                  <Alert variant="info" className="mb-0 small">
                    <i className="pi pi-shield me-2"></i>
                    <strong>Technical Note:</strong> Audio signals will be delivered regardless of browser tab focus status or window state.
                  </Alert>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}; 